import React from 'react';
import { NOTI_TYPE } from '@typings/notification/notificationType';
import styled from 'styled-components';
import { Inotification } from '@typings/notification/notification.interface';
import { User } from '@typings/setting/userType.interface';
import extractTextToHtml from '@utils/extractTextToHtml';
import { getJosaGa } from '@utils/josa';

const Content = styled.div`
  width: 100%;
  text-align: left;
`;
export const BlockStyle = styled.div<{ isSeen?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  background: var(--inkColor);
  margin-top: 8px;
  position: relative;

  span {
    overflow: hidden;
    white-space: normal;
    text-overflow: ellipsis;
    word-wrap: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }
`;

export const StrongStyle = styled.strong`
  max-width: 50%;
  overflow: hidden;
  white-space: normal;
  text-overflow: ellipsis;
  word-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  float: left;

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
    text-decoration: underline;
  }
`;

const DrawBox = ({
  senderData,
  affix,
  comment,
  isPresent,
  postingData,
  receiverData,
}: {
  senderData: any;
  affix?: string;
  comment?: string;
  isPresent?: boolean;
  receiverData?: any;
  postingData: {
    postingTitle: string;
    contents: string;
  };
}) => {
  return (
    <Content>
      {isPresent && receiverData ? (
        // 선물을 수령한 사람(계정)
        <strong>{receiverData.displayName || ''}</strong>
      ) : (
        affix && (
          <StrongStyle
            dangerouslySetInnerHTML={{
              __html: extractTextToHtml(postingData.postingTitle),
            }}
          />
        )
      )}
      {affix} <strong>{senderData.displayName || senderData.id}</strong>
      <span>{senderData.displayName ? getJosaGa(senderData.displayName) : '가'}</span>{' '}
      {comment}
      <BlockStyle>
        <span
          dangerouslySetInnerHTML={{
            __html: extractTextToHtml(postingData.contents),
          }}
        />
      </BlockStyle>
    </Content>
  );
};

type Props = {
  data: Inotification;
  senderData: User;
  receiverData: User | any;
  postData: any;
  numberId?: number;
};

function NotificationMessageMapper({
  postData,
  data,
  senderData,
  receiverData,
  numberId,
}: Props) {
  if (!senderData) return null;

  if (data.type === NOTI_TYPE.QUEST_REQUEST) {
    return (
      <Content>
        <strong>{senderData.displayName || senderData.id}</strong>
        <span>님이 퀘스트 검토를 요청했습니다.</span>
        <BlockStyle>
          <span
            dangerouslySetInnerHTML={{
              __html: extractTextToHtml(data.contents || ''),
            }}
          />
        </BlockStyle>
      </Content>
    );
  }

  if (!postData) return null;

  const rawTitle = postData.title
    ? postData.title
    : postData.contents
      ? extractTextToHtml(postData.contents)
      : '';

  const postingTitle = numberId && numberId > 0
    ? `[No.${numberId}] ${rawTitle}`
    : rawTitle;

  switch (data.type) {
    case NOTI_TYPE.COMMENT: {
      return (
        <DrawBox
          senderData={senderData}
          affix={'에'}
          comment={'댓글을 달았습니다.'}
          postingData={{ postingTitle, contents: data.contents }}
        />
      );
    }

    case NOTI_TYPE.REPLY: {
      return (
        <DrawBox
          senderData={senderData}
          affix={'에'}
          comment={'답글을 달았습니다.'}
          postingData={{ postingTitle, contents: data.contents }}
        />
      );
    }

    case NOTI_TYPE.TAGGED: {
      return (
        <DrawBox
          senderData={senderData}
          affix={''}
          comment={'당신을 태그했습니다.'}
          postingData={{ postingTitle, contents: data.contents }}
        />
      );
    }

    case NOTI_TYPE.GUESTBOOK: {
      return (
        <DrawBox
          senderData={senderData}
          affix={''}
          comment={'방명록에 새 글을 남겼습니다.'}
          postingData={{ postingTitle, contents: data.contents }}
        />
      );
    }

    case NOTI_TYPE.PRESENT: {
      return (
        <DrawBox
          senderData={senderData}
          affix={'에게'}
          comment={'선물을 보냈습니다.'}
          postingData={{ postingTitle, contents: data.contents }}
          receiverData={receiverData}
          isPresent
        />
      );
    }

    case NOTI_TYPE.BATTLE: {
      return (
        <DrawBox
          senderData={senderData}
          affix={'에서'}
          comment={'도전장을 보냈습니다. ✊✌✋'}
          postingData={{ postingTitle, contents: data.contents }}
        />
      );
    }

    default:
      return null;
  }
}

export default NotificationMessageMapper;
